import type {
  DeviceFieldEntry,
  DeviceMetadataEntry,
  InspectionOrder,
  InspectionOrderDevice
} from '../types/inspectionOrder';

export type RawInspectionOrder = Partial<InspectionOrder> & {
  id?: string;
  scheduled_start?: string;
  scheduled_at?: string;
  site_name?: string;
  locationName?: string;
  location_name?: string;
  site_phone?: string | null;
  locationPhone?: string | null;
  location_phone?: string | null;
  site_address?: string | null;
  siteAddress?: string | null;
  locationAddress?: string | null;
  location_address?: string | null;
  customer_name?: string;
  customer_phone?: string | null;
  customer_address?: string | null;
  billingAddress?: string | null;
  billing_address?: string | null;
  address?: string | null;
  status?: string | null;
  statusLabel?: string | null;
  status_label?: string | null;
  state_label?: string | null;
};

export type RawInspectionOrderDevice = Partial<InspectionOrderDevice> & {
  deviceId?: string | null;
  equipment_type?: string | null;
  equipmentTypeName?: string | null;
  equipment_type_name?: string | null;
  type?: string | null;
  typeName?: string | null;
  deviceType?: string | null;
  device_type?: string | null;
  identifier?: string | null;
  externalIdentifier?: string | null;
  external_id?: string | null;
  deviceIdentifier?: string | null;
  device_identifier?: string | null;
  metadata?: RawDeviceMetadataCollection;
  meta?: RawDeviceMetadataCollection;
  details?: RawDeviceMetadataCollection;
  attributes?: RawDeviceMetadataCollection;
  attributeSet?: RawDeviceMetadataCollection;
  attribute_set?: RawDeviceMetadataCollection;
  deviceTypeMetadata?: RawDeviceMetadataCollection;
  device_type_metadata?: RawDeviceMetadataCollection;
  deviceTypeDetails?: Record<string, unknown> | null;
  device_type_details?: Record<string, unknown> | null;
  equipmentTypeDetails?: Record<string, unknown> | null;
  equipment_type_details?: Record<string, unknown> | null;
  fields?: RawDeviceFieldCollection;
  fieldValues?: RawDeviceFieldCollection;
  field_values?: RawDeviceFieldCollection;
  predefinedFields?: RawDeviceFieldCollection;
  predefined_fields?: RawDeviceFieldCollection;
  deviceFields?: RawDeviceFieldCollection;
  device_fields?: RawDeviceFieldCollection;
  deviceTypeFields?: RawDeviceFieldCollection;
  device_type_fields?: RawDeviceFieldCollection;
  deviceTypeDefinition?: Record<string, unknown> | null;
  device_type_definition?: Record<string, unknown> | null;
  equipmentTypeDefinition?: Record<string, unknown> | null;
  equipment_type_definition?: Record<string, unknown> | null;
  values?: RawDeviceFieldCollection;
};

type RawDeviceKeyValueEntry = {
  label?: string | null;
  name?: string | null;
  key?: string | null;
  title?: string | null;
  displayName?: string | null;
  display_name?: string | null;
  fieldName?: string | null;
  field_name?: string | null;
  fieldLabel?: string | null;
  field_label?: string | null;
  group?: string | null;
  value?: unknown;
  valueLabel?: string | null;
  value_label?: string | null;
  displayValue?: string | null;
  display_value?: string | null;
  unit?: string | null;
  unitLabel?: string | null;
  unit_label?: string | null;
};

type RawDeviceMetadataCollection =
  | RawDeviceKeyValueEntry[]
  | Record<string, unknown>
  | null
  | undefined;

type RawDeviceFieldCollection =
  | RawDeviceKeyValueEntry[]
  | Record<string, unknown>
  | null
  | undefined;

const resolveString = (
  ...candidates: Array<string | null | undefined>
): string => {
  const value = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);
  return value ? value.trim() : '';
};

const resolveOptionalString = (
  ...candidates: Array<string | null | undefined>
): string | null => {
  const value = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);
  return value ? value.trim() : null;
};

const toOptionalString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0 ? value.trim() : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const prettifyKey = (key: string): string => {
  const cleaned = key.replace(/[_\-.]+/g, ' ').trim();
  if (!cleaned) {
    return key;
  }
  return cleaned
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const normalizeMetadataEntries = (value: RawDeviceMetadataCollection): DeviceMetadataEntry[] => {
  const toArray = (): RawDeviceKeyValueEntry[] => {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value;
    }
    if (isPlainObject(value)) {
      return Object.entries(value).map(([entryKey, entryValue]) => ({
        key: entryKey,
        value: toOptionalString(entryValue)
      }));
    }
    return [];
  };

  const entries = toArray();

  return entries
    .map((entry, index) => {
      const rawLabel = resolveOptionalString(
        entry.label ?? undefined,
        entry.name,
        entry.displayName,
        entry.display_name,
        entry.title,
        entry.key
      );
      const valueLabel =
        toOptionalString(entry.valueLabel) ??
        toOptionalString(entry.value_label) ??
        toOptionalString(entry.displayValue) ??
        toOptionalString(entry.display_value) ??
        toOptionalString(entry.value);
      const label =
        rawLabel ??
        (entry.key ? prettifyKey(entry.key) : null) ??
        (entry.name ? prettifyKey(entry.name) : null) ??
        `Eintrag ${index + 1}`;

      return {
        label,
        value: valueLabel
      };
    })
    .filter((entry): entry is DeviceMetadataEntry => !!entry.label);
};

const normalizeFieldEntries = (value: RawDeviceFieldCollection): DeviceFieldEntry[] => {
  const toArray = (): RawDeviceKeyValueEntry[] => {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value;
    }
    if (isPlainObject(value)) {
      return Object.entries(value).map(([entryKey, entryValue]) => ({
        key: entryKey,
        value: toOptionalString(entryValue)
      }));
    }
    return [];
  };

  const entries = toArray();

  return entries
    .map((entry, index) => {
      const rawName = resolveOptionalString(
        entry.label ?? undefined,
        entry.name,
        entry.fieldName,
        entry.field_name,
        entry.fieldLabel,
        entry.field_label,
        entry.displayName,
        entry.display_name,
        entry.title,
        entry.key
      );

      const valueLabel =
        toOptionalString(entry.valueLabel) ??
        toOptionalString(entry.value_label) ??
        toOptionalString(entry.displayValue) ??
        toOptionalString(entry.display_value) ??
        toOptionalString(entry.value);

      const normalizedUnit = resolveOptionalString(entry.unit ?? undefined, entry.unitLabel, entry.unit_label);
      const name =
        rawName ??
        (entry.key ? prettifyKey(entry.key) : null) ??
        (entry.name ? prettifyKey(entry.name) : null) ??
        `Feld ${index + 1}`;

      const normalized: DeviceFieldEntry = {
        name,
        value: valueLabel ?? null
      };

      if (normalizedUnit) {
        normalized.unit = normalizedUnit;
      }

      return normalized;
    })
    .filter((entry) => entry.name && entry.name.trim().length > 0);
};

const mergeMetadataCollections = (collections: Array<RawDeviceMetadataCollection>): DeviceMetadataEntry[] =>
  collections.flatMap((collection) => normalizeMetadataEntries(collection));

const mergeFieldCollections = (collections: Array<RawDeviceFieldCollection>): DeviceFieldEntry[] =>
  collections.flatMap((collection) => normalizeFieldEntries(collection));

export const normalizeInspectionOrderDevice = (
  device: RawInspectionOrderDevice | null | undefined
): InspectionOrderDevice | null => {
  if (!device) {
    return null;
  }

  const deviceTypeObject = [
    device.deviceTypeDetails,
    device.device_type_details,
    device.deviceTypeDefinition,
    device.device_type_definition,
    device.equipmentTypeDetails,
    device.equipment_type_details,
    device.deviceType,
    device.device_type
  ].find((value): value is Record<string, unknown> => isPlainObject(value));

  const deviceTypeMetadata = deviceTypeObject
    ? ((deviceTypeObject as any).metadata ??
      (deviceTypeObject as any).meta ??
      (deviceTypeObject as any).attributes ??
      (deviceTypeObject as any).attributeSet ??
      (deviceTypeObject as any).attribute_set ??
      (deviceTypeObject as any).details ??
      null)
    : null;

  const deviceTypeFields = deviceTypeObject
    ? ((deviceTypeObject as any).fields ??
      (deviceTypeObject as any).predefinedFields ??
      (deviceTypeObject as any).predefined_fields ??
      (deviceTypeObject as any).deviceFields ??
      (deviceTypeObject as any).device_fields ??
      (deviceTypeObject as any).fieldValues ??
      (deviceTypeObject as any).field_values ??
      (deviceTypeObject as any).values ??
      null)
    : null;

  const id = resolveOptionalString(device.id ?? undefined, device.deviceId);
  const identifier = resolveOptionalString(
    device.identifier,
    device.deviceIdentifier,
    device.device_identifier,
    device.externalIdentifier,
    device.external_id
  );
  const equipmentType = resolveOptionalString(
    device.equipmentType,
    device.equipment_type,
    device.equipmentTypeName,
    device.equipment_type_name,
    device.type,
    device.typeName,
    device.deviceType,
    device.device_type
  );

  if (!identifier && !equipmentType && !id) {
    return null;
  }

  const metadataSources: Array<RawDeviceMetadataCollection> = [
    device.metadata,
    device.meta,
    device.details,
    device.attributes,
    device.attributeSet,
    device.attribute_set,
    device.deviceTypeMetadata,
    device.device_type_metadata,
    deviceTypeMetadata
  ];

  const fieldsSources: Array<RawDeviceFieldCollection> = [
    device.fields,
    device.fieldValues,
    device.field_values,
    device.predefinedFields,
    device.predefined_fields,
    device.deviceFields,
    device.device_fields,
    device.deviceTypeFields,
    device.device_type_fields,
    device.values,
    deviceTypeFields
  ];

  return {
    id: id ?? null,
    identifier: identifier ?? null,
    equipmentType: equipmentType ?? null,
    nfcTagId: (device as any).nfcTagId ?? (device as any).nfc_tag_id ?? null,
    status: (device as any).status ?? null,
    statusLabel: (device as any).statusLabel ?? (device as any).status_label ?? null,
    inspectionResults: (device as any).inspectionResults ?? (device as any).inspection_results ?? null,
    lastSavedAt: (device as any).lastSavedAt ?? (device as any).last_saved_at ?? null,
    completedAt: (device as any).completedAt ?? (device as any).completed_at ?? null,
    completedByName: (device as any).completedByName ?? (device as any).completed_by_name ?? null,
    metadata: mergeMetadataCollections(metadataSources),
    fields: mergeFieldCollections(fieldsSources)
  };
};

export const normalizeInspectionOrder = (order: RawInspectionOrder | null | undefined): InspectionOrder | null => {
  if (!order) {
    return null;
  }

  const id = order.id;
  if (!id || typeof id !== 'string') {
    return null;
  }

  const stateCode = resolveOptionalString(order.state ?? undefined, order.status);
  const stateLabel = resolveOptionalString(
    order.stateLabel ?? undefined,
    order.state_label,
    order.statusLabel ?? undefined,
    order.status_label
  );

  return {
    id,
    scheduledStart: resolveString(order.scheduledStart, order.scheduled_start, order.scheduled_at),
    siteName: resolveString(order.siteName, order.site_name, order.locationName, order.location_name),
    sitePhone: resolveOptionalString(order.sitePhone, order.site_phone, order.locationPhone, order.location_phone),
    siteAddress: resolveOptionalString(
      order.siteAddress,
      order.site_address,
      order.locationAddress,
      order.location_address
    ),
    customerName: resolveString(order.customerName, order.customer_name),
    customerPhone: resolveOptionalString(order.customerPhone, order.customer_phone),
    customerAddress: resolveOptionalString(
      order.customerAddress,
      order.customer_address,
      order.billingAddress,
      order.billing_address,
      order.address
    ),
    state: stateCode,
    stateLabel
  };
};

export const normalizeInspectionOrders = (orders: unknown[]): InspectionOrder[] =>
  orders
    .map((order) => normalizeInspectionOrder(order as RawInspectionOrder))
    .filter((order): order is InspectionOrder => order !== null);
